import { useMemo } from 'react';
import { css } from '@emotion/react';
import { AnalyticsCommonTypes } from '@frontend/api-analytics';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore, WeaveLocation } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { CheckListPopoverSelector } from './check-list-popover-selector';

type AllAccessLocationSelectorProps = {
  defaultValue?: string[];
  disabled?: boolean;
  hideSearchBar?: boolean;
  label?: string;
  /**
   * Locations can be provided in TWO variants:
   * 1) Array<{ id, name }>
   * 2) StringRecord: { [locationId]: name } (from useCallIntelLocations)
   */
  locations?: Array<Pick<WeaveLocation, 'id' | 'name'>> | AnalyticsCommonTypes.StringRecord;
  onChange: (locations: string[]) => void;
  renderIcon?: (isFilterActive: boolean, isOpen?: boolean) => React.ReactNode;
  trackingId?: string;
  value?: string[];
};

const toOptionsFromArray = (items: Array<Pick<WeaveLocation, 'id' | 'name'>>): Record<string, string> => {
  const entries = (items ?? [])
    .filter((loc) => !!loc?.id)
    .map((loc) => [loc.id, loc.name ?? ''])
    .sort((a, b) => a[1].localeCompare(b[1]));
  return Object.fromEntries(entries);
};

const toOptionsFromMap = (map: Record<string, WeaveLocation>): Record<string, string> => {
  const entries = Object.values(map)
    .filter((loc) => !!loc?.id)
    .map((loc) => [loc.id, loc.name ?? ''])
    .sort((a, b) => a[1].localeCompare(b[1]));
  return Object.fromEntries(entries);
};

const toOptionsFromRecord = (rec: AnalyticsCommonTypes.StringRecord): Record<string, string> => {
  const entries = Object.entries(rec ?? {}).sort((a, b) => a[1].localeCompare(b[1]));
  return Object.fromEntries(entries);
};

export const AllAccessLocationSelector: React.FC<AllAccessLocationSelectorProps> = ({
  defaultValue,
  disabled,
  hideSearchBar = false,
  label,
  locations,
  onChange,
  renderIcon,
  trackingId,
  value,
}) => {
  const { t } = useTranslation('analytics');
  const { accessibleLocationData } = useAppScopeStore();

  const options = useMemo(() => {
    if (locations) {
      return Array.isArray(locations) ? toOptionsFromArray(locations) : toOptionsFromRecord(locations);
    }
    return toOptionsFromMap(accessibleLocationData);
  }, [accessibleLocationData, locations]);

  return (
    <CheckListPopoverSelector<string>
      defaultValue={defaultValue}
      disabled={disabled}
      onChange={onChange}
      value={value}
      options={options}
      label={label ?? t('Location')}
      hideSearchBar={hideSearchBar}
      renderIcon={renderIcon}
      customStyles={styles.locationSelector}
      trackingId={trackingId}
      infoText={t('Locations with AI Receptionist access')}
    />
  );
};

const styles = {
  locationSelector: {
    popoverMenuWrapper: css`
      max-width: 300px;
    `,
    chipDropdownFilter: css`
      width: 100%;
    `,
    checkListWrapper: css`
      max-height: 248px;
      overflow-y: auto;
    `,
    applyButton: css`
      margin-top: ${theme.spacing(1)};
    `,
    topBar: css`
      margin-bottom: ${theme.spacing(1)};
    `,
  },
};
